import type { EditorEdge, EditorGraph, EditorNode } from './editor-graph';
import { cloneGraph, PASTE_OFFSET } from './editor-graph';

export type Alignment = 'left' | 'center-x' | 'right' | 'top' | 'center-y' | 'bottom';
export type Distribution = 'horizontal' | 'vertical';

function nodeWidth(node: EditorNode): number {
  return node.measured?.width ?? node.width ?? 200;
}

function nodeHeight(node: EditorNode): number {
  return node.measured?.height ?? node.height ?? 88;
}

export function deleteSelectedNodes(
  graph: EditorGraph,
  selectedIds: ReadonlySet<string>,
): EditorGraph {
  const nodes = graph.nodes.filter((node) => !selectedIds.has(node.id));
  const remaining = new Set(nodes.map((node) => node.id));
  return {
    ...graph,
    nodes,
    edges: graph.edges.filter((edge) => remaining.has(edge.source) && remaining.has(edge.target)),
  };
}

/**
 * SPEC-006 EDGE-REQ-025 — deleting a selection removes selected nodes (and
 * any edge touching a removed node, cascading) together with any
 * explicitly-selected edges between surviving nodes.
 */
export function deleteSelection(
  graph: EditorGraph,
  selectedNodeIds: ReadonlySet<string>,
  selectedEdgeIds: ReadonlySet<string>,
): EditorGraph {
  const withoutNodes = deleteSelectedNodes(graph, selectedNodeIds);
  if (selectedEdgeIds.size === 0) return withoutNodes;
  return {
    ...withoutNodes,
    edges: withoutNodes.edges.filter((edge) => !selectedEdgeIds.has(edge.id)),
  };
}

export function setNodesLocked(
  graph: EditorGraph,
  selectedIds: ReadonlySet<string>,
  locked: boolean,
): EditorGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) =>
      selectedIds.has(node.id)
        ? {
            ...node,
            data: { ...node.data, __editorLocked: locked },
            locked,
            draggable: !locked,
          }
        : node,
    ),
  };
}

export function pasteNodes(
  graph: EditorGraph,
  clipboard: readonly EditorNode[],
  pasteIndex: number,
  createId: () => string = () => crypto.randomUUID(),
): { graph: EditorGraph; selectedIds: string[] } {
  if (clipboard.length === 0) return { graph, selectedIds: [] };
  const delta = PASTE_OFFSET * Math.max(1, pasteIndex);
  const clones = clipboard.map((node) => {
    const clone = structuredClone(node);
    return {
      ...clone,
      id: createId(),
      position: { x: clone.position.x + delta, y: clone.position.y + delta },
      selected: true,
      dragging: false,
    };
  });
  return {
    graph: { ...graph, nodes: [...graph.nodes, ...clones] },
    selectedIds: clones.map((node) => node.id),
  };
}

/**
 * SPEC-006 EDGE-REQ-026 — clipboard paste of a node selection also clones
 * any edge whose source AND target were both copied ("internal" edges),
 * remapped onto the pasted node IDs. Edges with an endpoint outside the
 * copied selection are dropped rather than left dangling.
 */
export function pasteNodesAndEdges(
  graph: EditorGraph,
  clipboardNodes: readonly EditorNode[],
  clipboardEdges: readonly EditorEdge[],
  pasteIndex: number,
  createId: () => string = () => crypto.randomUUID(),
): { graph: EditorGraph; selectedNodeIds: string[]; selectedEdgeIds: string[] } {
  if (clipboardNodes.length === 0) return { graph, selectedNodeIds: [], selectedEdgeIds: [] };
  const { graph: nextGraph, selectedIds } = pasteNodes(graph, clipboardNodes, pasteIndex, createId);
  const idMap = new Map(clipboardNodes.map((node, index) => [node.id, selectedIds[index]!]));
  const internalEdges = clipboardEdges.filter(
    (edge) => idMap.has(edge.source) && idMap.has(edge.target),
  );
  const clonedEdges = internalEdges.map((edge) => ({
    ...structuredClone(edge),
    id: createId(),
    source: idMap.get(edge.source)!,
    target: idMap.get(edge.target)!,
    selected: true,
  }));
  return {
    graph: { ...nextGraph, edges: [...nextGraph.edges, ...clonedEdges] },
    selectedNodeIds: selectedIds,
    selectedEdgeIds: clonedEdges.map((edge) => edge.id),
  };
}

export function alignNodes(
  graph: EditorGraph,
  selectedIds: ReadonlySet<string>,
  alignment: Alignment,
): EditorGraph {
  const selected = graph.nodes.filter((node) => selectedIds.has(node.id));
  if (selected.length < 2) return graph;
  const left = Math.min(...selected.map((node) => node.position.x));
  const right = Math.max(...selected.map((node) => node.position.x + nodeWidth(node)));
  const top = Math.min(...selected.map((node) => node.position.y));
  const bottom = Math.max(...selected.map((node) => node.position.y + nodeHeight(node)));
  const centerX = (left + right) / 2;
  const centerY = (top + bottom) / 2;

  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (!selectedIds.has(node.id) || node.locked) return node;
      const position = { ...node.position };
      if (alignment === 'left') position.x = left;
      if (alignment === 'center-x') position.x = centerX - nodeWidth(node) / 2;
      if (alignment === 'right') position.x = right - nodeWidth(node);
      if (alignment === 'top') position.y = top;
      if (alignment === 'center-y') position.y = centerY - nodeHeight(node) / 2;
      if (alignment === 'bottom') position.y = bottom - nodeHeight(node);
      return { ...node, position };
    }),
  };
}

export function distributeNodes(
  graph: EditorGraph,
  selectedIds: ReadonlySet<string>,
  direction: Distribution,
): EditorGraph {
  const selected = graph.nodes.filter((node) => selectedIds.has(node.id));
  if (selected.length < 3) return graph;
  const sorted = [...selected].sort((a, b) =>
    direction === 'horizontal' ? a.position.x - b.position.x : a.position.y - b.position.y,
  );
  const first = sorted[0]!;
  const last = sorted.at(-1)!;
  const firstCenter =
    direction === 'horizontal'
      ? first.position.x + nodeWidth(first) / 2
      : first.position.y + nodeHeight(first) / 2;
  const lastCenter =
    direction === 'horizontal'
      ? last.position.x + nodeWidth(last) / 2
      : last.position.y + nodeHeight(last) / 2;
  const step = (lastCenter - firstCenter) / (sorted.length - 1);
  const positions = new Map<string, { x: number; y: number }>();
  sorted.forEach((node, index) => {
    if (node.locked || index === 0 || index === sorted.length - 1) return;
    positions.set(
      node.id,
      direction === 'horizontal'
        ? { ...node.position, x: firstCenter + step * index - nodeWidth(node) / 2 }
        : { ...node.position, y: firstCenter + step * index - nodeHeight(node) / 2 },
    );
  });
  return {
    ...graph,
    nodes: graph.nodes.map((node) =>
      positions.has(node.id) ? { ...node, position: positions.get(node.id)! } : node,
    ),
  };
}

export function layoutGraph(graph: EditorGraph, direction: 'RIGHT' = 'RIGHT'): EditorGraph {
  if (direction !== 'RIGHT') return graph;
  const depth = new Map(graph.nodes.map((node) => [node.id, 0]));
  for (let pass = 0; pass < graph.nodes.length; pass += 1) {
    let changed = false;
    for (const edge of graph.edges) {
      const sourceDepth = depth.get(edge.source);
      const targetDepth = depth.get(edge.target);
      if (
        sourceDepth !== undefined &&
        targetDepth !== undefined &&
        targetDepth <= sourceDepth &&
        sourceDepth < graph.nodes.length - 1
      ) {
        depth.set(edge.target, sourceDepth + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  const rows = new Map<number, number>();
  return {
    ...cloneGraph(graph),
    nodes: graph.nodes.map((node) => {
      if (node.locked) return structuredClone(node);
      const column = depth.get(node.id) ?? 0;
      const row = rows.get(column) ?? 0;
      rows.set(column, row + 1);
      return { ...structuredClone(node), position: { x: column * 280, y: row * 136 } };
    }),
  };
}
